import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Youtube, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { DYNTRA_LOGO_URL } from "@/lib/assets";

const SOCIAL_LINKS = {
  linkedin: "https://www.linkedin.com/company/dyntra-store",
  instagram: "https://www.instagram.com/dyntrastore",
  facebook: "https://www.facebook.com/dyntrastore",
  youtube: "https://www.youtube.com/@dyntrastore",
};

const cols = [
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Careers", href: "/careers" },
      { label: "Blogs", href: "/blog" },
      { label: "Events", href: "/events" },
    ],
  },
  {
    title: "Customer Support",
    links: [
      { label: "Contact Us", href: "/contact" },
      { label: "Help Center", href: "/help" },
      { label: "FAQs", href: "/faqs" },
      { label: "Shipping Policy", href: "/policy/shipping" },
      { label: "Return Policy", href: "/policy/return" },
      { label: "Privacy Policy", href: "/policy/privacy" },
      { label: "Terms & Conditions", href: "/policy/terms" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Hire Hub", href: "/hire-hub" },
      { label: "Find Me Consultant", href: "/consultation" },
      { label: "Dr. Dyntra", href: "/ai-consultant" },
      { label: "Dyntra Connect", href: "/clinic-setup" },
      { label: "Refurbished", href: "/refurbished" },
      { label: "Events", href: "/events" },
    ],
  },
];

const NEWSLETTER_LS_KEY = "dyntra_newsletter_v1";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const onSubscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setStatus("loading");
    try {
      let ok = false;
      try {
        const res = await fetch("/api/newsletter/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: trimmed }),
        });
        ok = res.ok;
      } catch {
        // API not reachable — fall through to local persistence
      }
      const list = JSON.parse(localStorage.getItem(NEWSLETTER_LS_KEY) || "[]");
      if (!list.includes(trimmed)) list.push(trimmed);
      localStorage.setItem(NEWSLETTER_LS_KEY, JSON.stringify(list));
      setStatus("success");
      setEmail("");
      toast.success(ok ? "You're subscribed! We'll be in touch." : "Thanks — we saved you to our list.");
      setTimeout(() => setStatus("idle"), 3200);
    } catch (err) {
      setStatus("idle");
      toast.error("Could not subscribe right now. Please try again.");
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#111827] text-gray-300">
      <div className="dyntra-container py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-3 inline-block mb-4">
              <img src={DYNTRA_LOGO_URL} alt="Dyntra Store" className="h-11 w-auto" />
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              India's most trusted B2B dental ecommerce & services platform for dental professionals.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <SocialBtn icon={Linkedin} testid="footer-social-linkedin" href={SOCIAL_LINKS.linkedin} label="LinkedIn" />
              <SocialBtn icon={Instagram} testid="footer-social-instagram" href={SOCIAL_LINKS.instagram} label="Instagram" />
              <SocialBtn icon={Facebook} testid="footer-social-facebook" href={SOCIAL_LINKS.facebook} label="Facebook" />
              <SocialBtn icon={Youtube} testid="footer-social-youtube" href={SOCIAL_LINKS.youtube} label="YouTube" />
            </div>
            <div className="flex items-center gap-2 mt-5">
              <a href="#" data-testid="footer-google-play" className="inline-block">
                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-10" />
              </a>
              <a href="#" data-testid="footer-app-store" className="inline-block">
                <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" className="h-10" />
              </a>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="font-display text-sm font-bold text-white mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link data-testid={`footer-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`} to={l.href} className="text-sm text-gray-400 hover:text-white transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="font-display text-sm font-bold text-white mb-4">Newsletter</h4>
            <p className="text-sm text-gray-400 mb-3">Subscribe to get updates & offers.</p>
            <form
              onSubmit={onSubscribe}
              className="flex rounded-xl overflow-hidden border border-gray-200 focus-within:border-[#6B4CD9] bg-white"
            >
              <input
                data-testid="footer-newsletter-input"
                type="email"
                required
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === "loading"}
                className="flex-1 px-3 py-2.5 bg-white text-gray-900 placeholder-gray-400 text-sm outline-none disabled:opacity-60"
              />
              <button
                data-testid="footer-newsletter-submit"
                type="submit"
                disabled={status === "loading"}
                aria-label="Subscribe"
                className="px-4 bg-[#4B2AAD] hover:bg-[#6B4CD9] transition-colors flex items-center justify-center min-w-[46px]"
              >
                {status === "loading" ? (
                  <Loader2 className="h-4 w-4 text-white animate-spin" />
                ) : status === "success" ? (
                  <CheckCircle2 className="h-4 w-4 text-white" />
                ) : (
                  <ArrowRight className="h-4 w-4 text-white" />
                )}
              </button>
            </form>
            {status === "success" && (
              <p data-testid="footer-newsletter-success" className="mt-2 text-xs text-emerald-400">You're on the list — welcome to Dyntra!</p>
            )}
            <div className="mt-5">
              <p className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-wider">We Accept</p>
              <div className="flex items-center gap-2 flex-wrap">
                {["VISA", "Mastercard", "RuPay", "UPI", "Netbank"].map((p) => (
                  <div key={p} className="rounded-md border border-gray-700 px-2 py-1 text-[10px] font-bold text-gray-400 bg-gray-900/40">{p}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-800 py-4">
        <div className="dyntra-container flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <div>© 2026 Dyntra Store. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <Link to="/sitemap">Sitemap</Link>
            <Link to="/policy/privacy">Privacy Policy</Link>
            <Link to="/policy/terms">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SocialBtn({ icon: Icon, testid, href, label }) {
  return (
    <a
      data-testid={testid}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="h-9 w-9 rounded-full bg-gray-800 hover:bg-[#4B2AAD] flex items-center justify-center transition-colors"
    >
      <Icon className="h-4 w-4 text-gray-300" />
    </a>
  );
}
